/* ==========================================================================
   Sound Event Detector - Comprehensive Markets i18n Dictionary
   Multi-language articles across EN, CS, DE, ES, AR, PT
   ========================================================================== */

export const marketTranslations = {
  en: {
    healthcare: {
      title: "Acoustic AI Security for Healthcare & Hospitals",
      eyebrow: "HOSPITAL & PATIENT PROTECTION",
      lead: "SED alerts to aggression, panic screams, and glass break, enabling healthcare staff to respond faster and protect patients and employees.",
      overview: `
        <p>Healthcare facilities face rising verbal aggression and physical altercations in high-stress areas like Emergency Department waiting rooms and triage areas. Traditional CCTV cameras cannot trigger alerts until physical violence is already underway.</p>
        <p>Sound Event Detector provides instant early warning by detecting screaming and verbal panic in patient waiting areas. Because SED processes acoustic signals on the edge without recording human voice, it is 100% compliant with HIPAA and GDPR medical privacy regulations.</p>
      `,
      benefits: [
        "Early aggression detection before physical assault occurs",
        "Zero audio recording — 100% patient medical privacy compliant",
        "Protects ER nurses, doctors, and triage personnel 24/7",
        "Integrates directly into hospital security dispatch desks"
      ]
    },
    education: {
      title: "Acoustic Security & Threat Detection for Education & Campuses",
      eyebrow: "SCHOOL & CAMPUS SAFETY",
      lead: "SED detects gunshots, aggression, panic screams, and glass break, enabling schools to respond faster and protect students and staff.",
      overview: `
        <p>Primary and secondary schools, as well as university campuses, need rapid threat detection to help protect students, teachers, and other staff. During a serious security incident, every second matters, and timely alerts enable a faster, more coordinated response.</p>
        <p>SED sensors installed in hallways, cafeterias, and outdoor school areas can identify gunshots and panic screams in under five seconds and automatically send an alert to the connected security system. When sound localization is enabled, the system can also determine the location of the sound source. It is designed with privacy in mind and does not continuously record audio.</p>
      `,
      benefits: [
        "Gunshot alerts sent to the connected security system in under five seconds.",
        "Detection of panic screams and aggressive behavior in hallways, gyms, and other school areas.",
        "Privacy-conscious audio processing without continuous audio recording.",
        "Integration options for access control, public address, and other school security systems."
      ]
    },
    industrial: {
      title: "Acoustic Detection for Industry & Critical Infrastructure",
      eyebrow: "PERIMETER & CRITICAL ASSETS",
      lead: "SED alerts to gunshots, glass break, angle grinders, drills, and other hazard sounds that threaten operations, facilities, or property.",
      overview: `
        <p>Industrial perimeters stretching over thousands of meters are difficult to monitor using visual cameras alone. Intruders cut fence wiring under cover of fog, darkness, or foliage.</p>
        <p>SED nodes installed along perimeter fences detect angle grinder cutting, fence breach, and vehicle intrusion instantly, automatically slewing PTZ cameras to capture high-definition video evidence.</p>
      `,
      benefits: [
        "Instant detection of perimeter fence cutting & angle grinders",
        "Operates in heavy fog, rain, dust, and total darkness",
        "Direct integration with Axis, Hanwha, Milestone, and Genetec VMS",
        "Reduces false alarms caused by wildlife or wind"
      ]
    },
    public: {
      title: "Acoustic Detection for Cities & Public Spaces",
      eyebrow: "MUNICIPAL & PUBLIC SAFETY",
      lead: "SED detects gunshots, aggression, panic screams, and glass break, transmitting alerts for faster emergency response in public spaces.",
      overview: `
        <p>By pairing optical camera networks with directional sound event detection, municipal control centers gain instant acoustic situational awareness without invading citizen privacy or recording ambient conversations.</p>
        <p>When emergency sounds occur, PTZ cameras automatically slew to the sound source coordinates and alert dispatchers in under 5 seconds.</p>
      `,
      benefits: [
        "<5s alert dispatch to Municipal Police Command Centers",
        "Automated PTZ camera auto-tracking to sound source",
        "Covers public plazas, transport hubs, parks, and night-life districts",
        "100% GDPR compliant — zero ambient wiretapping"
      ]
    },
    private: {
      title: "Acoustic Detection for Commercial Properties & Private Estates",
      eyebrow: "COMMERCIAL REAL ESTATE & PRIVATE SITES",
      lead: "SED alerts to glass break, aggression, graffiti spray painting, and other hazard sounds, helping protect commercial and private properties.",
      overview: `
        <p>For corporate buildings and high-value private properties, SED provides an invisible perimeter ear that detects glass fracture, forced entry drilling, aerosol spraying, and alarms before intruders cross internal thresholds.</p>
        <p>Integrates natively with perimeter alarm systems, triggering high-decibel strobes and notifying private security teams in milliseconds.</p>
      `,
      benefits: [
        "Discreet aesthetic deployment with zero visual intrusion",
        "Instant alert notifications to private security guards",
        "Protects perimeter gates, underground garages, and glass facades",
        "Prevents false alarms caused by weather or pets"
      ]
    }
  },
  cs: {
    healthcare: {
      title: "Akustická AI bezpečnost pro zdravotnictví a nemocnice",
      eyebrow: "OCHRANA NEMOCNIC A ZDRAVOTNÍKŮ",
      lead: "SED upozorňuje na agresi, panický křik a tříštění skla, aby personál mohl rychleji reagovat a chránit pacienty i zaměstnance.",
      overview: `
        <p>Zdravotnická zařízení čelí narůstající slovní agresi a fyzickým konfliktům v exponovaných prostorách, jako jsou čekárny urgentních příjmů a triage koridory. Běžné kamerové systémy dokáží varovat až ve chvíli, kdy dochází k fyzickému násilí.</p>
        <p>Sound Event Detector poskytuje okamžité včasné varování detekcí panického křiku a agrese. Protože zpracovává akustické signály na edge čipu bez nahrávání lidské řeči, splňuje 100% GDPR a zdravotnické předpisy pro ochranu soukromí.</p>
      `,
      benefits: [
        "Ověřené nasazení ve Fakultní nemocnici Plzeň (FN Plzeň)",
        "Včasná detekce agrese dříve, než vypukne fyzické napadení personálu",
        "Kombinovaná ochrana vnitřního příjmu i venkovního perimetru sanitek",
        "Nulové nahrávání zvuku – 100% garance soukromí pacientů (GDPR)",
        "Přímá integrace do bezpečnostního velína nemocnice a tiché tísňové hlášení"
      ]
    },
    education: {
      title: "Akustická bezpečnost a detekce střelby pro školství a fakulty",
      eyebrow: "BEZPEČNOST ŠKOL A KAMPUSŮ",
      lead: "SED rozpoznává střelbu, agresi, panický křik a tříštění skla, aby školy mohly rychleji reagovat a chránit žáky i zaměstnance.",
      overview: `
        <p>Základní a střední školy i univerzitní kampusy potřebují rychlou detekci hrozeb, která pomáhá chránit žáky, studenty, pedagogy a další zaměstnance. Při závažné bezpečnostní události rozhodují sekundy a včasné předání upozornění umožňuje rychlejší a koordinovanější reakci.</p>
        <p>Senzory SED instalované na chodbách, v jídelnách a venkovních prostorách škol dokážou do pěti sekund rozpoznat výstřely a panický křik a automaticky předat upozornění do připojeného bezpečnostního systému. V konfiguraci s lokalizací mohou určit také polohu zdroje zvuku. Systém je navržen s ohledem na soukromí a neprovádí nepřetržité nahrávání zvuku.</p>
      `,
      benefits: [
        "Předání upozornění na detekovaný výstřel do připojeného bezpečnostního systému v čase do pěti sekund.",
        "Detekce panického křiku a agrese na chodbách, v tělocvičnách a dalších prostorách školy.",
        "Zpracování zvuku s ohledem na soukromí a bez nepřetržitého nahrávání.",
        "Možnost propojení s přístupovými, rozhlasovými a dalšími bezpečnostními systémy školy."
      ]
    },
    industrial: {
      title: "Akustická detekce pro průmysl a kritickou infrastrukturu",
      eyebrow: "OCHRANA PERIMETRU A KRITICKÉ INFRASTRUKTURY",
      lead: "SED upozorňuje na výstřely, tříštění skla, brusky, vrtačky a další rizikové zvuky, které mohou ohrozit provoz, areál nebo majetek.",
      overview: `
        <p>Průmyslové areály, energetická zařízení, sklady a další objekty kritické infrastruktury mají rozsáhlé a členité prostory, které nelze vždy spolehlivě kontrolovat pouze kamerami. Zvuková detekce pomáhá zachytit rizikovou událost také v místech se zhoršenou viditelností nebo mimo přímý záběr kamer.</p>
        <p>SED doplňuje kamerové a další bezpečnostní systémy o akustickou detekci. Dokáže rozpoznat charakteristické zvuky, například úhlovou brusku, výstřel nebo tříštění skla, a předat upozornění do připojeného bezpečnostního systému. Obsluha tak může situaci rychleji prověřit a zahájit odpovídající reakci.</p>
      `,
      benefits: [
        "Okamžitá detekce řezání plotu a úhlových brusek",
        "Funguje v husté mlze, dešti, prachu i absolutní tmě",
        "Nativní integrace s VMS systémy Axis, Hanwha, Milestone a Genetec",
        "Redukce falešných poplachů způsobených zvěří či větrem"
      ]
    },
    public: {
      title: "Akustická detekce pro města a veřejný prostor",
      eyebrow: "MĚSTA A VEŘEJNÁ BEZPEČNOST",
      lead: "SED rozpoznává výstřely, agresi, panický křik a tříštění skla a předává upozornění pro rychlejší reakci ve veřejném prostoru.",
      overview: `
        <p>Propojením městských kamerových systémů s akustickou detekcí SED získávají dispečinky okamžitý přehled o krizových událostech bez narušení soukromí občanů či záznamu hovorů.</p>
        <p>Systém v reálném čase identifikuje střelbu, panický křik, autonehody i sirény záchranných složek a navádí otočné PTZ kamery přímo na místo incidentu.</p>
      `,
      benefits: [
        "Předání poplachu na dispečink Městské policie a IZS za <5s",
        "Automatické natáčení PTZ kamer na místo zdroje zvuku",
        "Pokrývá náměstí, dopravní uzly, parky i ulice s nočním životem",
        "100% GDPR shoda – nulové nahrávání lidských hovorů"
      ]
    },
    private: {
      title: "Akustická detekce pro komerční objekty a soukromý majetek",
      eyebrow: "SOUKROMÝ A KOMERČNÍ SEKTOR",
      lead: "SED upozorňuje na tříštění skla, agresi, sprejování a další rizikové zvuky, a pomáhá tak chránit komerční i soukromé objekty.",
      overview: `
        <p>Pro komerční budovy a privátní nemovitosti poskytuje SED diskrétní perimetrickou ochranu, která detekuje tříštění skla, sprejování graffiti a pokusy o vloupání dříve, než pachatel pronikne do objektu.</p>
        <p>Propojuje se s bezpečnostními systémy a předává poplach bezpečnostní službě v řádu milisekund.</p>
      `,
      benefits: [
        "Diskrétní instalace bez narušení architektury budov",
        "Okamžitá notifikace bezpečnostní službě a ostraze",
        "Chrání vstupy, prosklené fasády, nádvoří a podzemní garáže",
        "Zabraňuje falešným poplachům od povětrnostních vlivů"
      ]
    }
  },
  de: {
    healthcare: {
      title: "Akustische KI-Sicherheit für Gesundheitswesen & Krankenhäuser",
      eyebrow: "KRANKENHAUS- UND PATIENTENSCHUTZ",
      lead: "SED warnt vor Aggressionen, Panikschreien und Glasbruch, damit das Personal schneller reagieren und Patienten sowie Mitarbeiter schützen kann.",
      overview: "<p>Frühzeitige Erkennung von Aggressionen in Notaufnahmen ohne Sprachaufzeichnung.</p>",
      benefits: ["Erkennt Aggression vor körperlicher Gewalt", "100% DSGVO-konform"]
    },
    education: {
      title: "Akustische Sicherheit für Schulen & Universitäten",
      eyebrow: "SCHUL- UND CAMPUS-SICHERHEIT",
      lead: "SED erkennt Schüsse, Aggressionen, Panikschreie und Glasbruch, damit Schulen schneller reagieren und Schüler sowie Personal schützen können.",
      overview: "<p>Schnelle Schusserkennung und Panikmeldung für Bildungseinrichtungen.</p>",
      benefits: ["Alarmierung in unter <5s", "100% DSGVO-konform"]
    },
    industrial: {
      title: "Akustische Detektion für Industrie & Kritische Infrastruktur",
      eyebrow: "INDUSTRIE & INFRASTRUKTUR",
      lead: "SED warnt vor Schüssen, Glasbruch, Winkelschleifern, Bohrern und weiteren Gefahrentönen, die Betrieb, Gelände oder Eigentum gefährden können.",
      overview: "<p>Zuverlässiger Perimeterschutz für Industrieanlagen und Versorger.</p>",
      benefits: ["Erkennt Trennschleifer und Zaunschnitt", "Funktioniert bei Nebel und Dunkelheit"]
    },
    public: {
      title: "Akustische Detektion für Städte & Öffentlichen Raum",
      eyebrow: "STÄDTE & ÖFFENTLICHE SICHERHEIT",
      lead: "SED erkennt Schüsse, Aggressionen, Panikschreie und Glasbruch und sendet Warnmeldungen für eine schnellere Reaktion im öffentlichen Raum.",
      overview: "<p>Echtzeit-Akustik für moderne Städte ohne Sprachüberwachung.</p>",
      benefits: ["Alarmierung in <5s", "DSGVO-konform ohne Sprachspeicherung"]
    },
    private: {
      title: "Akustische Detektion für Gewerbe & Private Liegenschaften",
      eyebrow: "GEWERBE & PRIVATER SEKTOR",
      lead: "SED warnt vor Glasbruch, Aggressionen, Graffiti-Sprayen und weiteren Risikotönen und schützt so gewerbliche sowie private Objekte.",
      overview: "<p>Akustischer Schutz für Gewerbeobjekte und Privatimmobilien.</p>",
      benefits: ["Diskrete Montage", "Sofortige Benachrichtigung der Security"]
    }
  },
  es: {
    healthcare: {
      title: "Seguridad Acústica con IA para Hospitales y Salud",
      eyebrow: "PROTECCIÓN HOSPITALARIA Y PACIENTES",
      lead: "SED alerta sobre agresiones, gritos de pánico y rotura de cristales para que el personal actúe con rapidez y proteja a pacientes y trabajadores.",
      overview: "<p>Detección temprana de agresiones en urgencias sin grabación de voz.</p>",
      benefits: ["Detección de agresión antes de violencia física", "100% compatible con RGPD"]
    },
    education: {
      title: "Seguridad Acústica para Escuelas y Campus",
      eyebrow: "SEGURIDAD EN ESCUELAS Y CAMPUS",
      lead: "SED reconoce disparos, agresiones, gritos de pánico y rotura de cristales para que los centros educativos actúen más rápido y protejan a alumnos y personal.",
      overview: "<p>Detección ultrarrápida de amenazas activas en entornos educativos.</p>",
      benefits: ["Despacho de alerta en <5s", "100% privacidad estudiantil RGPD"]
    },
    industrial: {
      title: "Detección Acústica para Industria e Infraestructuras Críticas",
      eyebrow: "INDUSTRIA E INFRAESTRUCTURA",
      lead: "SED alerta sobre disparos, rotura de cristales, radiales, taladros y otros sonidos de riesgo que puedan comprometer instalaciones o activos.",
      overview: "<p>Detección de corte de vallas y herramientas en perímetros industriales.</p>",
      benefits: ["Detecta radiales y corte de vallas", "Funciona en niebla y oscuridad"]
    },
    public: {
      title: "Detección Acústica para Ciudades y Espacios Públicos",
      eyebrow: "CIUDADES Y SEGURIDAD PÚBLICA",
      lead: "SED reconoce disparos, agresiones, gritos de pánico y rotura de cristales, enviando alertas para una respuesta más rápida en espacios públicos.",
      overview: "<p>Integración acústica para centros de control urbano sin invasión de la privacidad.</p>",
      benefits: ["Alerta en <5s a la policía", "100% compatible con RGPD"]
    },
    private: {
      title: "Detección Acústica para Edificios Comerciales y Propiedades Privadas",
      eyebrow: "SECTOR PRIVADO Y COMERCIAL",
      lead: "SED alerta sobre rotura de cristales, agresiones, aerosoles de grafiti y otros sonidos de riesgo, ayudando a proteger inmuebles comerciales y privados.",
      overview: "<p>Protección perimetral invisible para empresas y propiedades privadas.</p>",
      benefits: ["Instalación discreta", "Alerta inmediata a guardias"]
    }
  },
  pt: {
    healthcare: {
      title: "Segurança Acústica com IA para Saúde e Hospitais",
      eyebrow: "PROTEÇÃO HOSPITALAR E PACIENTES",
      lead: "O SED alerta para agressões, gritos de pânico e quebra de vidros, permitindo uma resposta rápida para proteger doentes e profissionais.",
      overview: "<p>Deteção precoce de agressões em serviços de urgência sem gravação de voz.</p>",
      benefits: ["Deteção de agressão antes de violência física", "100% compatível com RGPD"]
    },
    education: {
      title: "Segurança Acústica para Escolas e Universidades",
      eyebrow: "SEGURANÇA EM ESCOLAS E CAMPI",
      lead: "O SED reconhece disparos, agressões, gritos de pânico e quebra de vidros, permitindo às escolas responder com rapidez para proteger alunos e funcionários.",
      overview: "<p>Deteção ultrarrápida de incidentes em estabelecimentos de ensino.</p>",
      benefits: ["Alerta em <5s às forças de segurança", "100% privacidade estudantil RGPD"]
    },
    industrial: {
      title: "Deteção Acústica para Indústria e Infraestruturas Críticas",
      eyebrow: "INDÚSTRIA E INFRAESTRUTURAS",
      lead: "O SED alerta para disparos, quebra de vidros, rebarbadoras, berbequins e outros sons de risco que ameacem operações, instalações ou património.",
      overview: "<p>Deteção de corte de vedações e ferramentas pesadas em perímetros industriais.</p>",
      benefits: ["Deteta rebarbadoras e corte de vedações", "Funciona com nevoeiro e escuridão"]
    },
    public: {
      title: "Deteção Acústica para Cidades e Espaço Público",
      eyebrow: "CIDADES E SEGURANÇA PÚBLICA",
      lead: "O SED reconhece disparos, agressões, gritos de pânico e quebra de vidros, transmitindo alertas para uma resposta mais célere no espaço público.",
      overview: "<p>Monitorização acústica para centros de comando municipal com total privacidade.</p>",
      benefits: ["Alerta em <5s à polícia municipal", "100% compatível com RGPD"]
    },
    private: {
      title: "Deteção Acústica para Edifícios Comerciais e Propriedade Privada",
      eyebrow: "SETOR PRIVADO E COMERCIAL",
      lead: "O SED alerta para quebra de vidros, agressões, sprays de tinta e outros sons de risco, ajudando a proteger propriedades comerciais e privadas.",
      overview: "<p>Proteção perimetral discreta para empresas e propriedades privadas.</p>",
      benefits: ["Instalação discreta", "Alerta imediato às equipas de segurança"]
    }
  },
  ar: {
    healthcare: {
      title: "الأمان الصوتي بالذكاء الاصطناعي للمستشفيات والقطاع الصحي",
      eyebrow: "حماية المستشفيات والكوادر الطبية",
      lead: "ينبه SED إلى علامات الاعتداء وصراخ الذعر وتكسير الزجاج، مما يتيح للكوادر الطبية الاستجابة بسرعة أكبر وحماية المرضى والموظفين.",
      overview: "<p>كشف فوري للانفعالات والصراخ في غرف الانتظار دون تسجيل محادثات.</p>",
      benefits: ["كشف الاعتداء قبل وقوعه", "حماية كاملة للخصوصية 100%"]
    },
    education: {
      title: "الأمان الصوتي للمدارس والجامعات",
      eyebrow: "سلامة المدارس والحرم الجامعي",
      lead: "يتعرف SED على إطلاق النار والاعتداء وصراخ الذعر وتكسير الزجاج، لتمكين المؤسسات التعليمية من الاستجابة السريعة وحماية الطلاب والعاملين.",
      overview: "<p>كشف فوري للأحداث الطارئة في المدارس والجامعات مع احترام الخصوصية.</p>",
      benefits: ["إرسال التنبيه في أقل من 5 ثوانٍ", "100% حماية خصوصية الطلاب"]
    },
    industrial: {
      title: "الكشف الصوتي للصناعة والبنية التحتية الحيوية",
      eyebrow: "الصناعة والبنية التحتية",
      lead: "ينبه SED إلى إطلاق النار وتكسير الزجاج والمجالخ الزاوية والمثاقب وأصوات الخطر التي قد تهدد العمليات أو المنشآت أو الممتلكات.",
      overview: "<p>كشف محاولات اختراق الأسوار واستخدام قواطع المعادن في المنشآت الصناعية.</p>",
      benefits: ["كشف قطع الأسوار الفوري", "يعمل بكفاءة في الضباب والظلام"]
    },
    public: {
      title: "الكشف الصوتي للمدن والأماكن العامة",
      eyebrow: "المدن والأمن العام",
      lead: "يتعرف SED على إطلاق النار والاعتداء وصراخ الذعر وتكسير الزجاج، ويرسل تنبيهات فورية لتحقيق استجابة أسرع في الأماكن العامة.",
      overview: "<p>ربط كاميرات المدن بالكشف الصوتي لتوفير وعي فوري دون انتهاك الخصوصية.</p>",
      benefits: ["إرسال الإنذار في أقل من 5 ثوانٍ", "100% متوافق مع معايير الخصوصية"]
    },
    private: {
      title: "الكشف الصوتي للمباني التجارية والملكية الخاصة",
      eyebrow: "القطاع التجاري والخاص",
      lead: "ينبه SED إلى تكسير الزجاج والاعتداء ورش الجرافيتي وأصوات الخطر الأخرى، مما يساعد في حماية المنشآت التجارية والممتلكات الخاصة.",
      overview: "<p>حماية غير مرئية لكشف تكسير الزجاج ورذاذ الجرافيتي ومحاولات السطو.</p>",
      benefits: ["تركيب سري وغير مزعج", "إخطار فوري لأفراد الأمن"]
    }
  }
};
